import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, LockKeyhole, Star, MapPin, ArrowRight, BadgeCheck } from "lucide-react";
import { Eyebrow } from "./Reveal";

const LINES = [
  <>Get things done</>,
  <>
    by <span className="text-teal">trusted people</span>
  </>,
  <>near you.</>,
];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-teal/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-52 w-[420px] h-[420px] rounded-full bg-navy/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center py-16 md:py-24 lg:py-28">
        <div className="lg:col-span-6 relative z-10">
          <motion.div {...fade(0)}>
            <Eyebrow>The UK's local services marketplace</Eyebrow>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display font-extrabold tracking-[-0.03em] leading-[1.04] text-navy text-[2.6rem] sm:text-6xl lg:text-[4.4rem] xl:text-[4.9rem]"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            {...fade(0.7)}
            className="mt-6 text-base sm:text-lg text-slate-600 max-w-md leading-relaxed"
          >
            Post a job, receive offers from local professionals, and choose the person that's right for you.
          </motion.p>

          <motion.div {...fade(0.85)} className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href="#app"
              data-testid="hero-post-job-btn"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-hover text-navy font-semibold px-8 py-4 rounded-full shadow-lg shadow-teal/25 transition-colors duration-200"
            >
              Post a Job
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </a>
            <a
              href="#become-a-tasker"
              data-testid="hero-become-tasker-btn"
              className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              Become a Tasker
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-trust-line"
            {...fade(1)}
            className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500"
          >
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-teal" /> Verified people
            </span>
            <span className="inline-flex items-center gap-1.5">
              <LockKeyhole className="w-4 h-4 text-teal" /> Secure payments
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Star className="w-4 h-4 text-teal" /> Reviews you can trust
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 4 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-[480px]"
          >
            <motion.div style={{ y: yImg }} className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-navy/25 aspect-[4/5] bg-slate-200">
              <img
                src="https://images.pexels.com/photos/7491098/pexels-photo-7491098.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="A local professional assembling furniture in a modern home"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -top-4 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl shadow-navy/10 border border-slate-100 px-4 py-3 flex items-center gap-2.5 animate-float-slow"
              data-testid="hero-verified-chip"
            >
              <span className="w-8 h-8 rounded-full bg-teal/10 grid place-items-center">
                <BadgeCheck className="w-4 h-4 text-teal" />
              </span>
              <div>
                <p className="text-xs font-semibold text-navy">Identity verified</p>
                <p className="text-[11px] text-slate-500">Taskers you can trust</p>
              </div>
            </motion.div>

            <motion.div style={{ y: yCard }} className="absolute -bottom-8 -right-2 sm:-right-8">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-job-card"
                className="w-[240px] sm:w-[270px] bg-white rounded-2xl shadow-2xl shadow-navy/20 border border-slate-100 p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Job #2041</span>
                  <span className="text-[11px] font-semibold bg-teal/10 text-teal px-2.5 py-1 rounded-full">
                    3 offers
                  </span>
                </div>
                <h3 className="mt-2.5 font-display font-bold text-navy text-base">Furniture Assembly</h3>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="w-3.5 h-3.5 text-teal" /> NW London
                </p>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Budget</p>
                    <p className="font-display font-extrabold text-navy text-2xl">£70</p>
                  </div>
                  <div className="flex -space-x-2">
                    {["SK", "JM", "AR"].map((ini, i) => (
                      <span
                        key={ini}
                        className={`w-8 h-8 rounded-full border-2 border-white grid place-items-center text-[10px] font-bold text-white ${
                          ["bg-teal", "bg-navy", "bg-amber-500"][i]
                        }`}
                      >
                        {ini}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
